/**
 * idle-logoff — HIPAA automatic logoff (45 CFR 164.312(a)(2)(iii)).
 *
 * After a configurable window of NO user activity, the portal terminates the
 * session and redirects to the login page so PHI is never left on an unattended
 * screen. "Terminate" here is a FULL logoff: the cdx_session cookie is cleared
 * server-side (POST /api/auth/logout) AND the MSAL token cache is cleared, so
 * re-entry requires re-authentication. This matters most for the 2FA-exempt
 * identity, whose cookie is the only factor - clearing it is what actually
 * protects the unattended screen.
 *
 * Default window: 60 minutes. Tunable at build time via VITE_IDLE_LOGOFF_MINUTES
 * (a positive minute count, OR a word sentinel off/none/never/disabled/false to
 * turn the auto-logoff OFF entirely). William 2026-06-17: DISABLED (set to 'off'
 * in infra/deploy.ps1) - reviewers were being signed out mid-review; re-enable by
 * setting a positive minute count there and redeploying.
 *
 * Cross-tab: the last-activity timestamp is shared via localStorage, so
 * activity in any tab keeps every tab alive and a logoff in one is detected by
 * the others. Never arms in a /print/* render tab (headless, no user) or in
 * dev-bypass.
 */
import { useEffect } from 'react';
import { msalInstance, isDevAuthBypass } from '@auth/index';
import { isPrintContext } from '@lib/apiClient';

const DEFAULT_MINUTES = 60;
const LS_KEY = 'cdx_last_activity';
const RECORD_THROTTLE_MS = 5_000;   // coalesce high-frequency events (mousemove/scroll)
const CHECK_INTERVAL_MS = 30_000;   // how often we evaluate idle
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'click', 'mousemove'] as const;

/**
 * Resolve the idle window in ms from the build env, clamped to a sane floor.
 * An explicit WORD sentinel (off / none / never / disabled / false) returns 0,
 * which disables the auto-logoff (useIdleLogoff never arms). A stray 0 / negative
 * / non-numeric value is NOT a disable - it falls back to the safe default, so a
 * HIPAA control can never be turned off by a typo; only a deliberate word does it.
 */
export function resolveIdleTimeoutMs(rawEnv: unknown, defaultMinutes = DEFAULT_MINUTES): number {
  const sentinel = String(rawEnv ?? '').trim().toLowerCase();
  if (sentinel === 'off' || sentinel === 'none' || sentinel === 'never' || sentinel === 'disabled' || sentinel === 'false') {
    return 0;
  }
  const n = Number(rawEnv);
  const minutes = Number.isFinite(n) && n >= 1 ? n : defaultMinutes;
  return Math.floor(minutes) * 60_000;
}

/** Pure idle test — exported for unit coverage. */
export function isIdle(lastActivityMs: number, nowMs: number, timeoutMs: number): boolean {
  return nowMs - lastActivityMs >= timeoutMs;
}

/** Read the cross-tab last-activity stamp; fall back to "now" if unreadable. */
function readLastActivity(fallback: number): number {
  try {
    const v = Number(localStorage.getItem(LS_KEY));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeLastActivity(ts: number): void {
  try { localStorage.setItem(LS_KEY, String(ts)); } catch { /* storage may be blocked */ }
}

/** True only when there is an authenticated cdx_session identity to protect. */
function hasActiveSession(): boolean {
  try {
    return !!(window as unknown as { CDX_IDENTITY?: { email?: string } }).CDX_IDENTITY?.email;
  } catch {
    return false;
  }
}

async function performLogoff(): Promise<void> {
  // 1. Clear the MSAL second factor so re-entry requires re-authentication.
  try { await msalInstance.clearCache(); } catch { /* ignore */ }
  // 2. Clear the cdx_session cookie (HttpOnly -> server endpoint). keepalive so
  //    it still fires as we navigate away.
  try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include', keepalive: true }); } catch { /* ignore */ }
  // 3. Hard navigation: re-runs index.html bootAuth, which shows the inactivity
  //    notice (?reason=idle) and the re-open-your-link guidance.
  try { window.location.assign('/login?reason=idle'); } catch { /* ignore */ }
}

/**
 * useIdleLogoff — arm the inactivity auto-logoff for the lifetime of the app.
 * Mount once near the app root. Self-guards print/dev contexts and only fires
 * when an authenticated session exists.
 */
export function useIdleLogoff(): void {
  useEffect(() => {
    if (isDevAuthBypass() || isPrintContext()) return;
    const timeoutMs = resolveIdleTimeoutMs(import.meta.env.VITE_IDLE_LOGOFF_MINUTES);
    // Auto-logoff disabled by operator config (VITE_IDLE_LOGOFF_MINUTES=off,
    // William 2026-06-17): arm nothing, add no listeners, never auto-logoff.
    if (timeoutMs <= 0) return;

    let lastRecord = 0;
    let firing = false;
    const record = () => {
      const t = Date.now();
      if (t - lastRecord < RECORD_THROTTLE_MS) return;
      lastRecord = t;
      writeLastActivity(t);
    };
    // Seed: mounting/navigating is itself activity.
    writeLastActivity(Date.now());
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, record, { passive: true });

    const id = window.setInterval(() => {
      if (firing) return;
      if (!hasActiveSession()) return; // nothing to protect on public/login pages
      if (isIdle(readLastActivity(Date.now()), Date.now(), timeoutMs)) {
        firing = true;
        void performLogoff();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, record);
      window.clearInterval(id);
    };
  }, []);
}
